import { InstancedBufferAttribute, InstancedBufferGeometry, Mesh, Vector2, Vector3, Vector4 } from 'three';
import type { Object3D, ShaderMaterial, Texture } from 'three';
import { butterflyGeometry, createButterflyMaterial } from '@cloudpearl/assets';
import type { SharedUniforms } from '@cloudpearl/assets';

/** A few dozen butterflies fluttering over the meadow around the camera (GPU-animated). */
export class Butterflies {
  readonly mesh: Mesh;
  private readonly center: Vector3;

  constructor(parent: Object3D, shared: SharedUniforms, heightfield: Texture, count: number) {
    const base = butterflyGeometry();
    const g = new InstancedBufferGeometry();
    g.index = base.index;
    g.setAttribute('position', base.getAttribute('position'));
    const seeds = new Float32Array(count * 4);
    for (let i = 0; i < seeds.length; i++) seeds[i] = Math.random();
    g.setAttribute('aSeed', new InstancedBufferAttribute(seeds, 4));
    g.instanceCount = count;
    // White, sulphur yellow, orange, blue.
    const mat = createButterflyMaterial(shared, heightfield, ['#f4f1e8', '#f2d14c', '#ec8a2c', '#6f9be6']);
    this.mesh = new Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.center = mat.uniforms['uBoxCenter']!.value as Vector3;
    parent.add(this.mesh);
  }

  follow(camPos: Vector3, forward: Vector3): void {
    this.center.copy(camPos).addScaledVector(forward, 16);
  }

  /**
   * Keep butterflies out of a capsule from the camera to just past the pearl (the menu shot),
   * or let them roam (`pearl` null). Eases in and out.
   */
  keepClear(camPos: Vector3, pearl: Vector3 | null, forward: Vector3, dt: number): void {
    const u = (this.mesh.material as ShaderMaterial).uniforms;
    const k = u['uAvoid']!.value as Vector2;
    k.x = 4.5;
    k.y += ((pearl ? 1 : 0) - k.y) * Math.min(1, dt * 2);
    if (pearl) (u['uAvoidSeg']!.value as Vector4).set(camPos.x, camPos.z, pearl.x + forward.x * 2, pearl.z + forward.z * 2);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    (this.mesh.material as ShaderMaterial).dispose();
  }
}
